import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Calendar, Clock, MapPin, Wallet, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Compact card to showcase a track on the homepage / cross-promote pages.
 */
export default function TrackCard({ track, href, ctaLabel = "View track", accent = "terracotta" }) {
  const reduced = useReducedMotion();
  const isTerra = accent === "terracotta";

  return (
    <motion.article
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`track-card-${track.slug}`}
      className="relative flex flex-col bg-paper rounded-3xl border border-[rgba(74,63,57,0.10)] shadow-[0_10px_30px_rgba(31,26,23,0.06)] hover:shadow-[0_18px_50px_rgba(31,26,23,0.10)] transition-shadow overflow-hidden"
    >
      {/* Top accent stripe */}
      <div
        className="h-1.5 w-full"
        style={{
          background: isTerra
            ? "linear-gradient(90deg, #C86A4A 0%, #E7A487 100%)"
            : "linear-gradient(90deg, #5F7F6B 0%, #B9CDBE 100%)",
        }}
      />

      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide"
            style={{
              backgroundColor: isTerra ? "rgba(200,106,74,0.14)" : "rgba(95,127,107,0.18)",
              color: isTerra ? "#8a4128" : "#3f5a4d",
            }}
          >
            <Sparkles className="w-3 h-3" /> {track.name}
          </span>
          <Badge variant="secondary" className="bg-[rgba(242,215,182,0.45)] text-[#8a4128] border-0">
            Cohort 02 · Dec 2026
          </Badge>
        </div>

        <h3 className="mt-4 font-display text-2xl sm:text-3xl text-ink leading-snug">
          {track.fullName}
        </h3>
        <p className="mt-2 font-display italic text-[15px] text-terracotta">
          {track.tagline}
        </p>
        <p className="mt-4 text-ink-muted leading-[1.7] text-[15px]">{track.shortPitch}</p>

        <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
          <Row icon={<Calendar className="w-4 h-4" />} label={`${track.startDate} – ${track.endDate}`} />
          <Row icon={<Clock className="w-4 h-4" />} label={track.commitment} />
          <Row icon={<MapPin className="w-4 h-4" />} label="Hybrid · Gurgaon" />
          <Row
            icon={track.feeINR ? <Wallet className="w-4 h-4" /> : <Users className="w-4 h-4" />}
            label={track.feeINR ? `INR ${track.feeINR.toLocaleString()}` : "Selection-based"}
          />
        </ul>

        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
            For
          </p>
          <ul className="flex flex-wrap gap-2">
            {track.audience.map((a) => (
              <li
                key={a}
                className="text-[12.5px] px-3 py-1.5 rounded-full bg-[rgba(74,63,57,0.05)] text-ink-muted border border-[rgba(74,63,57,0.10)]"
              >
                {a}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto pt-7">
          <Link to={href}>
            <Button
              data-testid={`track-card-cta-${track.slug}`}
              className={`w-full rounded-xl text-white transition-colors ${
                isTerra
                  ? "bg-[#C86A4A] hover:bg-[#B85F41]"
                  : "bg-[#5F7F6B] hover:bg-[#506e5c]"
              }`}
            >
              {ctaLabel} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

function Row({ icon, label }) {
  return (
    <li className="flex items-center gap-2 text-ink">
      <span className="text-terracotta">{icon}</span>
      <span className="truncate">{label}</span>
    </li>
  );
}
